import { getErrorMessage } from '../utils/errorHandler';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Mail, Send, Plus, Trash2, Upload, Download, Search, Users, FolderOpen, History, Filter, X } from 'lucide-react';
import { ContactsCentreTable, ContactsProjetsTable, CampaignsTable } from '../components/MailingTables';
import { ContactCentreDialog, ContactProjetDialog } from '../components/MailingContactDialogs';
import { SendDialog } from '../components/MailingSend';
import { TestDialog, CampaignDetail } from '../components/MailingTest';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Mailing() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('centre');
  
  // Data
  const [tags, setTags] = useState([]);
  const [settings, setSettings] = useState({ default_sender_email: '', default_sender_name: 'Vitalis Sports' });
  const [contactsCentre, setContactsCentre] = useState([]);
  const [contactsProjets, setContactsProjets] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  
  // Filters
  const [searchCentre, setSearchCentre] = useState('');
  const [searchProjets, setSearchProjets] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [selectedCivilites, setSelectedCivilites] = useState([]);
  const [selectedClassements, setSelectedClassements] = useState([]);
  const [filterAgeMin, setFilterAgeMin] = useState('');
  const [filterAgeMax, setFilterAgeMax] = useState('');
  const [includeNoDate, setIncludeNoDate] = useState(true);
  
  // Selection
  const [selectedCentre, setSelectedCentre] = useState([]);
  const [selectedProjets, setSelectedProjets] = useState([]);
  
  // Sorting
  const [sortCentre, setSortCentre] = useState({ field: 'nom', dir: 'asc' });
  const [sortProjets, setSortProjets] = useState({ field: 'nom', dir: 'asc' });
  
  // Dialogs
  const [contactCentreDialog, setContactCentreDialog] = useState(false);
  const [contactProjetDialog, setContactProjetDialog] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [testDialog, setTestDialog] = useState(false);
  const [campaignDetail, setCampaignDetail] = useState(null);
  
  // Editing
  const [editContactCentre, setEditContactCentre] = useState(null);
  const [editContactProjet, setEditContactProjet] = useState(null);
  
  // Send form
  const [sendForm, setSendForm] = useState({ sender_email: '', sender_name: 'Vitalis Sports', subject: '', html_content: '<p>Ihre Nachricht hier...</p>' });
  const [sendAttachments, setSendAttachments] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  
  const fileRef = useRef(null);
  const importRef = useRef(null);

  // Fetch data
  useEffect(() => { fetchTags(); fetchSettings(); fetchContactsCentre(); fetchContactsProjets(); fetchCampaigns(); fetchImages(); }, []);
  useEffect(() => { fetchContactsCentre(); }, [searchCentre, selectedTags, includeDisabled]);
  useEffect(() => { fetchContactsProjets(); }, [searchProjets]);

  const fetchTags = async () => { try { const r = await axios.get(`${API}/mailing/tags`); setTags(r.data); } catch {} };
  const fetchSettings = async () => { 
    try { 
      const r = await axios.get(`${API}/mailing/settings`); 
      setSettings(r.data); 
      setSendForm(f => ({ ...f, sender_email: r.data.default_sender_email || '', sender_name: r.data.default_sender_name || 'Vitalis Sports' })); 
    } catch {} 
  };
  const fetchContactsCentre = async () => { 
    try { 
      const p = new URLSearchParams(); 
      if (searchCentre) p.append('search', searchCentre); 
      if (selectedTags.length > 0) p.append('tags', selectedTags.join(',')); 
      if (includeDisabled) p.append('include_disabled', 'true'); 
      const r = await axios.get(`${API}/mailing/contacts/centre?${p}`); 
      setContactsCentre(r.data); 
    } catch {} 
  };
  const fetchContactsProjets = async () => { try { const r = await axios.get(`${API}/mailing/contacts/projets${searchProjets ? `?search=${searchProjets}` : ''}`); setContactsProjets(r.data); } catch {} };
  const fetchCampaigns = async () => { try { const r = await axios.get(`${API}/mailing/campaigns`); setCampaigns(r.data); } catch {} };
  const resumeCampaign = async (c) => { if (!window.confirm(`Reprendre l'envoi de « ${c.subject} » ? (${c.sent_count}/${c.total_recipients} déjà envoyés)`)) return; try { const r = await axios.post(`${API}/mailing/campaigns/${c.id}/resume`); toast.success(`Reprise de l'envoi (${r.data.remaining} restants). Suivez la progression ici.`); fetchCampaigns(); const iv = setInterval(fetchCampaigns, 5000); setTimeout(() => clearInterval(iv), 120000); } catch (e) { toast.error(getErrorMessage(e, 'Fehler')); } };
  const fetchImages = async () => { try { const r = await axios.get(`${API}/mailing/images`); setUploadedImages(r.data); } catch {} };

  // Contact handlers
  const saveContactCentre = async () => { try { editContactCentre?.id ? await axios.put(`${API}/mailing/contacts/centre/${editContactCentre.id}`, editContactCentre) : await axios.post(`${API}/mailing/contacts/centre`, editContactCentre); toast.success('Gespeichert'); setContactCentreDialog(false); setEditContactCentre(null); fetchContactsCentre(); } catch (e) { toast.error(getErrorMessage(e, 'Fehler')); } };
  const deleteContactCentre = async (id) => { if (!confirm('Löschen?')) return; try { await axios.delete(`${API}/mailing/contacts/centre/${id}`); toast.success('Gelöscht'); fetchContactsCentre(); } catch { toast.error('Fehler'); } };
  const importCentre = async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append('file', f); try { const r = await axios.post(`${API}/mailing/contacts/centre/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`${r.data.imported} importiert`); fetchContactsCentre(); } catch { toast.error('Fehler'); } e.target.value = ''; };
  const exportCentre = async () => { try { const r = await axios.get(`${API}/mailing/contacts/centre/export`, { responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'kontakte_vitalis.xlsx'; document.body.appendChild(a); a.click(); a.remove(); } catch { toast.error('Fehler'); } };
  
  const saveContactProjet = async () => { try { editContactProjet?.id ? await axios.put(`${API}/mailing/contacts/projets/${editContactProjet.id}`, editContactProjet) : await axios.post(`${API}/mailing/contacts/projets`, editContactProjet); toast.success('Gespeichert'); setContactProjetDialog(false); setEditContactProjet(null); fetchContactsProjets(); } catch (e) { toast.error(getErrorMessage(e, 'Fehler')); } };
  const deleteContactProjet = async (id) => { if (!confirm('Löschen?')) return; try { await axios.delete(`${API}/mailing/contacts/projets/${id}`); toast.success('Gelöscht'); fetchContactsProjets(); } catch { toast.error('Fehler'); } };
  const clearProjets = async () => { if (!confirm('Datenbank leeren?')) return; try { const r = await axios.delete(`${API}/mailing/contacts/projets`); toast.success(r.data.message); fetchContactsProjets(); } catch { toast.error('Fehler'); } };
  const importProjets = async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append('file', f); try { const r = await axios.post(`${API}/mailing/contacts/projets/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`${r.data.imported} importiert`); fetchContactsProjets(); } catch { toast.error('Fehler'); } e.target.value = ''; };
  const exportProjets = async () => { try { const r = await axios.get(`${API}/mailing/contacts/projets/export`, { responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'kontakte_swiss_tennis.xlsx'; document.body.appendChild(a); a.click(); a.remove(); } catch { toast.error('Fehler'); } };
  
  const uploadImage = async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append('file', f); try { const r = await axios.post(`${API}/mailing/images/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Hochgeladen'); fetchImages(); setSendForm(prev => ({ ...prev, html_content: prev.html_content + `<img src="${r.data.url}" style="max-width:100%;" />` })); } catch { toast.error('Fehler'); } e.target.value = ''; };
  
  const openSend = () => { const ids = activeTab === 'centre' ? selectedCentre : selectedProjets; if (ids.length === 0) { toast.error('Kontakte auswählen'); return; } setSendDialog(true); };
  const openTest = () => { setTestEmail(user?.email || ''); setTestDialog(true); };
  const sendTest = async () => { if (!testEmail) { toast.error('E-Mail erforderlich'); return; } setSending(true); try { await axios.post(`${API}/mailing/send/test`, { ...sendForm, test_email: testEmail, attachment_ids: sendAttachments.map(a => a.id) }); toast.success(`Test gesendet`); setTestDialog(false); } catch (e) { toast.error(getErrorMessage(e, 'Fehler')); } setSending(false); };
  const sendCampaign = async () => { const ids = activeTab === 'centre' ? selectedCentre : selectedProjets; if (!sendForm.subject.trim()) { toast.error('Betreff erforderlich'); return; } setSending(true); setSendProgress({ total: ids.length, sent: 0, failed: 0 }); try { const r = await axios.post(`${API}/mailing/send/campaign`, { ...sendForm, contact_ids: ids, contact_type: activeTab, attachment_ids: sendAttachments.map(a => a.id) }); toast.success(`Envoi lancé pour ${r.data.total} destinataires. Suivez la progression dans Verlauf.`); activeTab === 'centre' ? setSelectedCentre([]) : setSelectedProjets([]); fetchCampaigns(); const iv = setInterval(fetchCampaigns, 5000); setTimeout(() => clearInterval(iv), 180000); setTimeout(() => { setSendDialog(false); setSendProgress(null); }, 1500); } catch (e) { toast.error(getErrorMessage(e, 'Fehler')); setSendProgress(null); } setSending(false); };
  const viewCampaign = async (id) => { try { const r = await axios.get(`${API}/mailing/campaigns/${id}`); setCampaignDetail(r.data); } catch {} };

  // Calculate age from date of birth
  const calculateAge = (dateStr) => {
    if (!dateStr) return null;
    const birth = new Date(dateStr);
    if (isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Filter contacts by civilite, classement, and age
  const filterContacts = (contacts) => {
    return contacts.filter(c => {
      // Filter by civilite (multi-select)
      if (selectedCivilites.length > 0 && !selectedCivilites.includes(c.civilite || '')) return false;
      // Filter by classement (multi-select)
      if (selectedClassements.length > 0 && !selectedClassements.includes(c.classement_tennis || '')) return false;
      // Filter by age
      const age = calculateAge(c.date_naissance);
      if (filterAgeMin || filterAgeMax) {
        if (age === null) return includeNoDate;
        if (filterAgeMin && age < parseInt(filterAgeMin)) return false;
        if (filterAgeMax && age > parseInt(filterAgeMax)) return false;
      }
      return true;
    });
  };

  // Sorting and filtering
  const filteredCentre = filterContacts(contactsCentre);
  const sortedCentre = [...filteredCentre].sort((a, b) => { const av = a[sortCentre.field] || ''; const bv = b[sortCentre.field] || ''; return sortCentre.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
  const sortedProjets = [...contactsProjets].sort((a, b) => { const av = a[sortProjets.field] || ''; const bv = b[sortProjets.field] || ''; return sortProjets.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
  
  const toggleSortCentre = (f) => setSortCentre({ field: f, dir: sortCentre.field === f && sortCentre.dir === 'asc' ? 'desc' : 'asc' });
  const toggleSortProjets = (f) => setSortProjets({ field: f, dir: sortProjets.field === f && sortProjets.dir === 'asc' ? 'desc' : 'asc' });

  // Available Anrede options (German)
  const CIVILITES = ['Herr', 'Frau'];
  
  // Get unique classements for filter dropdown
  const uniqueClassements = [...new Set(contactsCentre.filter(c => c.classement_tennis).map(c => c.classement_tennis))];

  // Clear all filters
  const clearFilters = () => {
    setSelectedCivilites([]);
    setSelectedClassements([]);
    setFilterAgeMin('');
    setFilterAgeMax('');
    setIncludeNoDate(true);
    setSelectedTags([]);
  };

  const hasActiveFilters = selectedCivilites.length > 0 || selectedClassements.length > 0 || filterAgeMin || filterAgeMax || selectedTags.length > 0;

  return (
    <div className="space-y-6" data-testid="mailing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="h-6 w-6" />E-Mail-Kampagnen</h1>
          <p className="text-muted-foreground">Verwalten Sie Ihre Kontakte und senden Sie Kampagnen</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="centre"><Users className="h-4 w-4 mr-2" />Vitalis</TabsTrigger>
          <TabsTrigger value="projets"><FolderOpen className="h-4 w-4 mr-2" />Swiss Tennis</TabsTrigger>
          <TabsTrigger value="historique"><History className="h-4 w-4 mr-2" />Verlauf</TabsTrigger>
        </TabsList>

        <TabsContent value="centre" className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Suchen..." value={searchCentre} onChange={e => setSearchCentre(e.target.value)} className="pl-9 w-48" />
              </div>
              {/* Multi-select tags filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-40 justify-start" data-testid="filter-tags-btn">
                    <Filter className="h-4 w-4 mr-2" />
                    {selectedTags.length === 0 ? 'Tags' : `${selectedTags.length} Tag(s)`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2">
                  <div className="space-y-2">
                    {tags.map(t => (
                      <label key={t.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-muted rounded">
                        <Checkbox 
                          checked={selectedTags.includes(t.name)} 
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedTags([...selectedTags, t.name]);
                            } else {
                              setSelectedTags(selectedTags.filter(x => x !== t.name));
                            }
                          }}
                        />
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: t.color }} />
                        <span className="text-sm">{t.name}</span>
                      </label>
                    ))}
                    {selectedTags.length > 0 && (
                      <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setSelectedTags([])}>
                        Löschen
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {/* Anrede filter - multi-select */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-36 justify-start" data-testid="filter-anrede">
                    <Filter className="h-4 w-4 mr-2" />
                    {selectedCivilites.length === 0 ? 'Anrede' : `${selectedCivilites.length} gew.`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2">
                  <div className="space-y-2">
                    {CIVILITES.map(c => (
                      <label key={c} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-muted rounded">
                        <Checkbox 
                          checked={selectedCivilites.includes(c)} 
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedCivilites([...selectedCivilites, c]);
                            } else {
                              setSelectedCivilites(selectedCivilites.filter(x => x !== c));
                            }
                          }}
                        />
                        <span className="text-sm">{c}</span>
                      </label>
                    ))}
                    {selectedCivilites.length > 0 && (
                      <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setSelectedCivilites([])}>
                        Löschen
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {/* Klassierung filter - multi-select */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-36 justify-start" data-testid="filter-klassierung">
                    <Filter className="h-4 w-4 mr-2" />
                    {selectedClassements.length === 0 ? 'Klassierung' : `${selectedClassements.length} gew.`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2">
                  <div className="space-y-2">
                    {uniqueClassements.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">Keine Klassierung</p>
                    ) : (
                      uniqueClassements.map(c => (
                        <label key={c} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-muted rounded">
                          <Checkbox 
                            checked={selectedClassements.includes(c)} 
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedClassements([...selectedClassements, c]);
                              } else {
                                setSelectedClassements(selectedClassements.filter(x => x !== c));
                              }
                            }}
                          />
                          <span className="text-sm">{c}</span>
                        </label>
                      ))
                    )}
                    {selectedClassements.length > 0 && (
                      <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setSelectedClassements([])}>
                        Löschen
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {/* Age filter popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-32 justify-start" data-testid="filter-age-btn">
                    <Users className="h-4 w-4 mr-2" />
                    {(filterAgeMin || filterAgeMax) ? `${filterAgeMin || '0'}-${filterAgeMax || '∞'}` : 'Alter'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3">
                  <div className="space-y-3">
                    <div className="text-sm font-medium">Nach Alter filtern</div>
                    <div className="flex gap-2 items-center">
                      <Input 
                        type="number" 
                        placeholder="Min" 
                        value={filterAgeMin} 
                        onChange={e => setFilterAgeMin(e.target.value)} 
                        className="w-20"
                        data-testid="filter-age-min"
                      />
                      <span className="text-muted-foreground">-</span>
                      <Input 
                        type="number" 
                        placeholder="Max" 
                        value={filterAgeMax} 
                        onChange={e => setFilterAgeMax(e.target.value)} 
                        className="w-20"
                        data-testid="filter-age-max"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={includeNoDate} onCheckedChange={setIncludeNoDate} data-testid="include-no-date" />
                      Ohne Datum einschliessen
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                  <X className="h-4 w-4 mr-1" />Filter löschen
                </Button>
              )}
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={includeDisabled} onCheckedChange={setIncludeDisabled} />Inaktive anzeigen</label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => importRef.current?.click()}><Upload className="h-4 w-4 mr-2" />Import</Button>
              <input ref={importRef} type="file" accept=".xls,.xlsx" onChange={importCentre} className="hidden" />
              <Button variant="outline" size="sm" onClick={exportCentre}><Download className="h-4 w-4 mr-2" />Export</Button>
              <Button size="sm" onClick={() => { setEditContactCentre({ nachname: '', vorname: '', email: '', anrede: '', telefon: '', geburtsdatum: '', klassierung: '', disabled: false, tags: [] }); setContactCentreDialog(true); }}><Plus className="h-4 w-4 mr-2" />Hinzufügen</Button>
            </div>
          </div>
          {selectedCentre.length > 0 && <div className="bg-primary/10 p-4 rounded-lg flex items-center justify-between"><span className="font-medium">{selectedCentre.length} ausgewählt</span><Button onClick={openSend}><Send className="h-4 w-4 mr-2" />Senden</Button></div>}
          <ContactsCentreTable contacts={sortedCentre} selected={selectedCentre} onSelect={(id, ch) => setSelectedCentre(ch ? [...selectedCentre, id] : selectedCentre.filter(x => x !== id))} onSelectAll={() => setSelectedCentre(selectedCentre.length === sortedCentre.length ? [] : sortedCentre.map(c => c.id))} sort={sortCentre} onSort={toggleSortCentre} tags={tags} onEdit={(c) => { setEditContactCentre(c); setContactCentreDialog(true); }} onDelete={deleteContactCentre} />
        </TabsContent>

        <TabsContent value="projets" className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Suchen..." value={searchProjets} onChange={e => setSearchProjets(e.target.value)} className="pl-9 w-64" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => document.getElementById('imp-proj').click()}><Upload className="h-4 w-4 mr-2" />Import</Button>
              <input id="imp-proj" type="file" accept=".xls,.xlsx" onChange={importProjets} className="hidden" />
              <Button variant="outline" size="sm" onClick={exportProjets}><Download className="h-4 w-4 mr-2" />Export</Button>
              <Button variant="destructive" size="sm" onClick={clearProjets}><Trash2 className="h-4 w-4 mr-2" />Leeren</Button>
              <Button size="sm" onClick={() => { setEditContactProjet({ nachname: '', vorname: '', email: '', anrede: '', geschlecht: '', sprache: '', klassierung: '', alterskategorie: '', regionalverband: '' }); setContactProjetDialog(true); }}><Plus className="h-4 w-4 mr-2" />Hinzufügen</Button>
            </div>
          </div>
          {selectedProjets.length > 0 && <div className="bg-primary/10 p-4 rounded-lg flex items-center justify-between"><span className="font-medium">{selectedProjets.length} ausgewählt</span><Button onClick={openSend}><Send className="h-4 w-4 mr-2" />Senden</Button></div>}
          <ContactsProjetsTable contacts={sortedProjets} selected={selectedProjets} onSelect={(id, ch) => setSelectedProjets(ch ? [...selectedProjets, id] : selectedProjets.filter(x => x !== id))} onSelectAll={() => setSelectedProjets(selectedProjets.length === sortedProjets.length ? [] : sortedProjets.map(c => c.id))} sort={sortProjets} onSort={toggleSortProjets} onEdit={(c) => { setEditContactProjet(c); setContactProjetDialog(true); }} onDelete={deleteContactProjet} />
        </TabsContent>

        <TabsContent value="historique" className="space-y-4">
          <CampaignsTable campaigns={campaigns} onView={viewCampaign} onResume={resumeCampaign} />
        </TabsContent>
      </Tabs>

      <ContactCentreDialog open={contactCentreDialog} onClose={() => setContactCentreDialog(false)} contact={editContactCentre} setContact={setEditContactCentre} tags={tags} onSave={saveContactCentre} />
      <ContactProjetDialog open={contactProjetDialog} onClose={() => setContactProjetDialog(false)} contact={editContactProjet} setContact={setEditContactProjet} onSave={saveContactProjet} />
      <SendDialog open={sendDialog} onClose={() => setSendDialog(false)} form={sendForm} setForm={setSendForm} images={uploadedImages} fileRef={fileRef} onUpload={uploadImage} progress={sendProgress} sending={sending} onTest={openTest} onSend={sendCampaign} count={activeTab === 'centre' ? selectedCentre.length : selectedProjets.length} apiUrl={API.replace('/api', '')} attachments={sendAttachments} setAttachments={setSendAttachments} />
      <TestDialog open={testDialog} onClose={() => setTestDialog(false)} email={testEmail} setEmail={setTestEmail} sending={sending} onSend={sendTest} />
      <CampaignDetail open={!!campaignDetail} onClose={() => setCampaignDetail(null)} c={campaignDetail} />
    </div>
  );
}
